import { Inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account';
import { ENVIRONMENT, filterBoolean } from '@recruitee/common';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags/data';
import { FeatureGuardFacade, GuardedFeature } from '@recruitee/feature-guard';
import {
  Company,
  OfferStage,
  OfferStatus,
  ReferralsSettings,
  Stage,
  StageCategory,
} from '@recruitee/http/types';
import {
  ReferralsSettingsEntityActions,
  ReferralsSettingsEntitySelectors,
} from '@recruitee/referrals/data';

export interface ReferralsEnvironment {
  referralsProtocol: 'http' | 'https';
  referralsPartialDomain: string;
}

@Injectable({ providedIn: 'root' })
export class ReferralsService {
  public referralsSettings$: Observable<ReferralsSettings> = this.store
    .select(ReferralsSettingsEntitySelectors.data)
    .pipe(filterBoolean);
  public referralsPortalUrl$: Observable<string> = this.referralsSettings$.pipe(
    map(settings => this.createPortalUrl(settings.subdomain)),
  );

  private currentCompany: Company;
  private hasReferrals: boolean;
  private hasReferralsFlag = injectDevFlag(DevFlag.Referrals);

  constructor(
    private store: Store,
    private featureFacade: FeatureGuardFacade,
    @Inject(ENVIRONMENT) private environment: ReferralsEnvironment,
  ) {
    const currentCompany$ = this.store.select(AccountSelectors.currentCompany);

    currentCompany$.pipe(filterBoolean, first()).subscribe((currentCompany: Company) => {
      if (currentCompany.settings.referralProgramEnabled) {
        this.store.dispatch(ReferralsSettingsEntityActions.fetch());
      }
    });
    currentCompany$.pipe(filterBoolean).subscribe((currentCompany: Company) => {
      this.currentCompany = currentCompany;
    });

    this.featureFacade.checkFeatureAvailability(GuardedFeature.Referrals).subscribe(enabled => {
      this.hasReferrals = enabled;
    });
  }

  public hasReferralsAndReferralsFlag(): boolean {
    return this.hasReferrals && this.hasReferralsFlag;
  }

  public isProgramEnabled(): boolean {
    return (
      this.currentCompany! &&
      this.currentCompany!.settings.referralProgramEnabled &&
      this.hasReferralsAndReferralsFlag()
    );
  }

  public isReferralsEnabledForOfferStatus(offerStatus: OfferStatus): boolean {
    return ![OfferStatus.Archived, OfferStatus.Closed].includes(offerStatus);
  }

  public createPortalUrl(subdomain: string | null): string {
    const _subdomain = subdomain || 'example';
    const { referralsProtocol, referralsPartialDomain } = this.environment;
    return `${referralsProtocol}://${_subdomain}.${referralsPartialDomain}`;
  }

  public filterOutReferralStage(
    stages: OfferStage[] | Array<Partial<Stage>>,
  ): OfferStage[] | Array<Partial<Stage>> {
    return stages.filter(
      s =>
        s.category !== StageCategory.Referred ||
        (s.category === StageCategory.Referred && this.hasReferralsAndReferralsFlag()),
    );
  }

  public checkReferralsFeatureFlag(stages: Stage[]): Stage[] {
    return stages.filter(
      stage =>
        stage.category !== StageCategory.Referred ||
        (stage.category === StageCategory.Referred && this.hasReferralsAndReferralsFlag()),
    );
  }
}
